/**
 * <LiveDot> — la pastille du direct (atom, `proposé`).
 *
 * Un point vert plein, et autour un halo qui s'élargit et s'efface en boucle : ce qu'il accompagne se
 * lit en temps réel (Wajih, daily du 23/10 : les élèves connectés d'un live). Il ne dit rien seul — le
 * texte à côté porte le sens, et le lecteur d'écran le lit sur lui ; le point est décoratif.
 *
 * À `<NotificationDot>` « il y a du neuf », à lui « c'est en ce moment » : deux sens, deux atomes.
 *
 * Le battement est un rythme de CONTENU, pas une durée de la marque (`tokens/motion` → `livePulse`, à côté
 * du souffle de la pause zen, `breath`). En mouvement réduit, le point seul, posé.
 *
 * Il bat tout le live : sur l'app, par le pilote natif ; au web, qui n'en a pas, par une animation CSS que le
 * navigateur joue seul (mesuré dans la story de l'en-tête : la boucle d'`Animated` y coûtait 25 à 30 ms de
 * script par seconde et 120 recalculs de style, à côté de la vidéo du live).
 */
import React, { useEffect, useMemo, useRef } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { livePulse } from "../../theme/tokens/motion"
import { injectStyleOnce } from "../../utils/injectStyleOnce"

/** Le halo s'efface à cette taille du point. */
const HALO_SCALE = 2.6
const HALO_OPACITY = 0.45

/** Le battement au web, le même qu'au natif : `Easing.out(Easing.quad)` s'y écrit en `cubic-bezier`. */
const WEB_BEAT = "masteur-live-dot-beat"
const WEB_BEAT_CSS = `@keyframes ${WEB_BEAT} {
  from { opacity: ${HALO_OPACITY}; transform: scale(1); }
  to { opacity: 0; transform: scale(${HALO_SCALE}); }
}`

export type LiveDotProps = {
    /** Diamètre du point en px — défaut 8. */
    size?: number
    testID?: string
}

export function LiveDot(props: LiveDotProps) {
    const { size = 8, testID } = props
    const reduceMotion = useReduceMotion()
    const web = Platform.OS === "web"
    const pulse = useRef(new Animated.Value(0)).current

    useEffect(() => {
        if (reduceMotion) {
            pulse.setValue(0)
            return undefined
        }
        if (web) {
            injectStyleOnce(WEB_BEAT, WEB_BEAT_CSS)
            return undefined
        }
        const loop = Animated.loop(
            Animated.timing(pulse, {
                toValue: 1,
                duration: livePulse.period,
                easing: Easing.out(Easing.quad),
                useNativeDriver: true,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [reduceMotion, web, pulse])
    // Gardé d'un rendu à l'autre : une interpolation neuve se rattacherait au pilote natif à chaque rendu.
    const beat = useMemo(
        () => ({
            opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [HALO_OPACITY, 0] }),
            transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, HALO_SCALE] }) }],
        }),
        [pulse]
    )

    return (
        <View style={styles.root(size)} testID={testID} pointerEvents="none" accessible={false}>
            {!reduceMotion &&
                (web ? (
                    <View style={[styles.dot(size), styles.halo, styles.webBeat]} />
                ) : (
                    <Animated.View style={[styles.dot(size), styles.halo, beat]} />
                ))}
            <View style={styles.dot(size)} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le halo déborde du point sans prendre de place : la ligne garde la hauteur du point. */
    root: (size: number) => ({ width: size, height: size }),
    /** Le halo : un second point, posé sur le premier. */
    halo: { position: "absolute", top: 0, left: 0 },
    webBeat: {
        _web: {
            animationName: WEB_BEAT,
            animationDuration: `${livePulse.period}ms`,
            animationIterationCount: "infinite",
            animationTimingFunction: "cubic-bezier(0.5, 1, 0.89, 1)",
        },
    },
    dot: (size: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: theme.colors.success,
    }),
}))
